import { createNoise3D } from "simplex-noise";

// Extended DNA interface including void parameters for Awoken assets
export interface DNA {
  dna1: number;
  dna2: number;
  dna3: number;
  dna4: number;
  dna5: number;
  dna6: number;
  dna7: number;
  dna8: number;
  dna9: number;
  dna10: number;
  dna11: number;
  dna12: number;
  dna13: number;
  void1?: number;
  void2?: number;
  void3?: number;
}

// Default DNA fallback values
export const DEFAULT_DNA: DNA = {
  dna1: 233,
  dna2: 269,
  dna3: 571,
  dna4: 771,
  dna5: 0.6,
  dna6: 49,
  dna7: 0.23,
  dna8: 0.83,
  dna9: 333,
  dna10: 775,
  dna11: 218,
  dna12: 39,
  dna13: 249,
};

// Awoken policy IDs for detection
const AWOKEN_POLICY_IDS = [
  "95c248e17f0fc35be4d2a7d186a84cdcda5b99d7ad2799ebe98a9865",
  "2b4ac45ffa2c8dca38354121bb581ce582819106c48920b4bcf3a109",
];

/**
 * Extract DNA values from metadata JavaScript source code string
 * The metadata contains JS code like: dna1=340,dna2=582,...,void1=215,void2=278,void3=.329...
 */
export function extractDNAFromMetadata(metadataString: string): DNA {
  const dna: DNA = { ...DEFAULT_DNA };

  if (!metadataString || typeof metadataString !== "string") {
    return dna;
  }

  // Regex patterns to extract dna and void values
  const dnaPatterns = [
    /dna1=(\d+\.?\d*)/,
    /dna2=(\d+\.?\d*)/,
    /dna3=(\d+\.?\d*)/,
    /dna4=(\d+\.?\d*)/,
    /dna5=(\d+\.?\d*)/,
    /dna6=(\d+\.?\d*)/,
    /dna7=(\d+\.?\d*)/,
    /dna8=(\d+\.?\d*)/,
    /dna9=(\d+\.?\d*)/,
    /dna10=(\d+\.?\d*)/,
    /dna11=(\d+\.?\d*)/,
    /dna12=(\d+\.?\d*)/,
    /dna13=(\d+\.?\d*)/,
    /void1=(\d+\.?\d*)/,
    /void2=(\d+\.?\d*)/,
    /void3=(\d+\.?\d*)/,
  ];

  const keys: (keyof DNA)[] = [
    "dna1", "dna2", "dna3", "dna4", "dna5", "dna6", "dna7", "dna8",
    "dna9", "dna10", "dna11", "dna12", "dna13", "void1", "void2", "void3",
  ];

  dnaPatterns.forEach((pattern, index) => {
    const match = metadataString.match(pattern);
    if (match && match[1]) {
      const value = parseFloat(match[1]);
      if (!isNaN(value)) {
        (dna as any)[keys[index]] = value;
      }
    }
  });

  return dna;
}

/**
 * Check if a collection is an Awoken asset based on policy ID
 */
export function isAwokenAsset(collectionData: any): boolean {
  if (!collectionData?.policy) {
    return false;
  }
  return AWOKEN_POLICY_IDS.includes(collectionData.policy);
}

/**
 * Generate Awoken background canvas from DNA
 * Default size 256x256 to match the original asset resolution
 */
export function generateAwokenCanvas(
  dna: DNA,
  size: number = 256
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return canvas;
  }

  const noise3D = createNoise3D();

  // Create image data for direct pixel manipulation
  const imageData = ctx.createImageData(size, size);
  const data = imageData.data;

  // Initialize all pixels to black with full alpha (matching original canvas start state)
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 0;     // R
    data[i + 1] = 0; // G
    data[i + 2] = 0; // B
    data[i + 3] = 255; // A
  }

  // Use actual Awoken rendering logic
  const t = 0; // Static frame
  const { dna1, dna2, dna3, dna4, dna5, dna6, dna7, dna8, dna9, dna10, dna11, dna12, dna13, void1 = 300, void2 = 112, void3 = 0.149 } = dna;

  for (let a = 0; a < size; a++) {
    for (let d = 0; d < size; d++) {
      const i = 4 * (a + size * d);

      // Two layers of noise (matching original Awoken algorithm)
      const n = noise3D(a / dna1, d / dna2, t / dna9) * dna5 + dna7;
      const e = noise3D(a / dna3, d / dna4, t / dna10) * dna6 + dna8;

      // Red channel - original: 10<dna12?n*dna11:n*e*dna11
      let r: number;
      if (10 < dna12) {
        r = n * dna11;
      } else {
        r = n * e * dna11;
      }

      // Green channel - original: n*e*dna12
      const g = n * e * dna12;

      // Blue channel - original: 0===dna11||0===dna12?n*e*dna13:dna13
      let b: number;
      if (dna11 === 0 || dna12 === 0) {
        b = n * e * dna13;
      } else {
        b = dna13;
      }

      // Alpha channel - original: void3<.01?800*simplex.noise3D(a/void1,d/void2,t/dna9)+dna7:255
      let v: number;
      if (void3 < 0.01) {
        v = 800 * noise3D(a / void1, d / void2, t / dna9) + dna7;
      } else {
        v = 255;
      }

      // Assign directly - Uint8ClampedArray handles clamping automatically
      data[i + 0] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = v;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

/**
 * Composite overlay image onto background canvas at specified position and scale
 */
export function compositeImages(
  backgroundCanvas: HTMLCanvasElement,
  overlayImage: HTMLImageElement,
  positionX: number, // 0-100 percentage
  positionY: number, // 0-100 percentage
  scale: number // 10-200 percentage
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = backgroundCanvas.width;
  canvas.height = backgroundCanvas.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return canvas;
  }

  // Draw background
  ctx.drawImage(backgroundCanvas, 0, 0);

  // Calculate overlay dimensions based on scale percentage
  const baseSize = Math.min(canvas.width, canvas.height);
  const overlayWidth = (baseSize * scale) / 100;
  const overlayHeight = (baseSize * scale) / 100;

  // Calculate position to center based on percentage
  // 50%, 50% means centered
  const centerX = (canvas.width * positionX) / 100;
  const centerY = (canvas.height * positionY) / 100;
  const x = centerX - overlayWidth / 2;
  const y = centerY - overlayHeight / 2;

  // Draw overlay image with transparency support
  ctx.drawImage(overlayImage, x, y, overlayWidth, overlayHeight);

  return canvas;
}

/**
 * Download canvas as JPEG
 */
export function downloadCanvasAsJPEG(canvas: HTMLCanvasElement, filename: string = "profile-picture.jpg"): void {
  const link = document.createElement("a");
  link.download = filename;
  link.href = canvas.toDataURL("image/jpeg", 0.95);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}